export const FALLBACK_CATALOG = {
  fiat: [{ code: "AED", kind: "FIAT", name: "UAE Dirham", min_decimals: 2, qty_decimals: 2 }],
  crypto: [{ code: "USDT", kind: "CRYPTO", name: "Tether", min_decimals: 2, qty_decimals: 6 }],
  pairs: [{ base_asset: "USDT", quote_asset: "AED" }],
};

const CRYPTO_ICONS = {
  USDT: "/images/option-img/usdt_icon.svg",
  USDC: "/images/convert-img/usd_coin.svg",
  BTC: "/images/market-img/bitcoin_icon.svg",
  ETH: "/images/convert-img/ethereum.svg",
  XRP: "/images/convert-img/xrp_icon.svg",
  SOL: "/images/convert-img/solana_icon.svg",
  DOGE: "/images/convert-img/doge_icon.svg",
  BNB: "/images/AnnouncementImg/bnb_icon.svg",
};

const ASSET_NAMES = {
  AED: "UAE Dirham",
  USDT: "Tether",
  USDC: "USD Coin",
  BTC: "Bitcoin",
  ETH: "Ethereum",
  XRP: "XRP",
  SOL: "Solana",
};

export function cryptoIcon(code) {
  return CRYPTO_ICONS[String(code || "").toUpperCase()] || "/images/coin_icon.svg";
}

export function assetName(code) {
  const k = String(code || "").toUpperCase();
  return ASSET_NAMES[k] || k;
}

function normalizeAsset(row, kind) {
  const code = String(row?.code || "").toUpperCase();
  if (!code) return null;
  return {
    code,
    kind: String(row?.kind || kind || "").toUpperCase(),
    name: row?.name || assetName(code),
    min_decimals: Number(row?.min_decimals) || 2,
    qty_decimals: Number(row?.qty_decimals) || (kind === "FIAT" ? 2 : 6),
    icon: kind === "CRYPTO" ? cryptoIcon(code) : null,
  };
}

export function normalizeCatalog(data) {
  const src = data && typeof data === "object" ? data : {};
  const fiat = (Array.isArray(src.fiat) ? src.fiat : []).map((r) => normalizeAsset(r, "FIAT")).filter(Boolean);
  const crypto = (Array.isArray(src.crypto) ? src.crypto : []).map((r) => normalizeAsset(r, "CRYPTO")).filter(Boolean);
  const pairs = (Array.isArray(src.pairs) ? src.pairs : [])
    .map((p) => ({
      base_asset: String(p?.base_asset || "").toUpperCase(),
      quote_asset: String(p?.quote_asset || "").toUpperCase(),
      base_name: p?.base_name || "",
      quote_name: p?.quote_name || "",
      pair_name: p?.pair_name || "",
    }))
    .filter((p) => p.base_asset && p.quote_asset);
  if (!fiat.length || !crypto.length || !pairs.length) return FALLBACK_CATALOG;
  return { fiat, crypto, pairs };
}

export function cryptosForFiat(catalog, quoteAsset) {
  const quote = String(quoteAsset || "").toUpperCase();
  const allowed = new Set(
    (catalog?.pairs || []).filter((p) => p.quote_asset === quote).map((p) => p.base_asset)
  );
  return (catalog?.crypto || []).filter((c) => allowed.has(c.code));
}

export function fiatsForCrypto(catalog, baseAsset) {
  const base = String(baseAsset || "").toUpperCase();
  const allowed = new Set(
    (catalog?.pairs || []).filter((p) => p.base_asset === base).map((p) => p.quote_asset)
  );
  return (catalog?.fiat || []).filter((f) => allowed.has(f.code));
}

export function convertDeepLink(side, asset) {
  const s = String(side || "buy").toLowerCase() === "sell" ? "sell" : "buy";
  const a = String(asset || "USDT").toUpperCase() || "USDT";
  return `/buy_crypto?side=${s}&asset=${encodeURIComponent(a)}`;
}

export function footerBuySellItems(catalog) {
  const src = catalog && catalog.pairs?.length ? catalog : FALLBACK_CATALOG;
  const items = [];
  src.pairs.forEach((p) => {
    const name = p.base_name || assetName(p.base_asset);
    items.push({
      id: `buy-${p.base_asset}-${p.quote_asset}`,
      title: `Buy ${name} (${p.base_asset})`,
      to: convertDeepLink("buy", p.base_asset),
    });
    items.push({
      id: `sell-${p.base_asset}-${p.quote_asset}`,
      title: `Sell ${name} (${p.base_asset})`,
      to: convertDeepLink("sell", p.base_asset),
    });
  });
  return items;
}

export function sanitizeAmountInput(raw) {
  const s = String(raw || "").replace(/,/g, "").replace(/[^\d.]/g, "");
  const first = s.indexOf(".");
  if (first === -1) return s;
  return `${s.slice(0, first + 1)}${s.slice(first + 1).replace(/\./g, "")}`;
}

export function formatQuoteAmount(value, decimals) {
  const n = Number.parseFloat(String(value ?? "0"));
  if (!Number.isFinite(n)) return "0";
  const d = Number.isFinite(Number(decimals)) ? Math.max(0, Math.min(18, Number(decimals))) : 2;
  return n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
}

export function quoteSecondsLeft(expiresAt) {
  if (!expiresAt) return 0;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (!Number.isFinite(ms)) return 0;
  return Math.max(0, Math.ceil(ms / 1000));
}

/** U10 — BPS label only (not money math). 50 → "0.50%". */
export function feeBpsPercentLabel(feeBps) {
  const n = Number(feeBps);
  if (!Number.isFinite(n) || n < 0) return "";
  return `${(n / 100).toFixed(2)}%`;
}

/** U10 — mid / gross line from quote API strings. */
export function quoteMidLine(quote, fiatCode, cryptoCode) {
  if (!quote) return "";
  const side = String(quote.side || "").toUpperCase();
  const spend = String(quote.you_spend ?? "");
  const gross = String(quote.gross_receive ?? "");
  if (!spend || !gross) return "";
  if (side === "SELL") {
    return `${spend} ${cryptoCode || "USDT"} = ${gross} ${fiatCode || "AED"}`;
  }
  return `${spend} ${fiatCode || "AED"} = ${gross} ${cryptoCode || "USDT"}`;
}

/** U10 — fee line: AED amount + optional BPS % (never invent % for FLAT). */
export function quoteFeeLabel(quote, formatAed) {
  if (!quote) return "—";
  const fee = typeof formatAed === "function" ? formatAed(quote.fee_aed) : String(quote.fee_aed ?? "0");
  const type = String(quote.fee_type || "").toUpperCase();
  if (type === "BPS") {
    const pct = feeBpsPercentLabel(quote.fee_bps);
    return pct ? `${fee} AED (${pct})` : `${fee} AED`;
  }
  return `${fee} AED`;
}
